import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CatalogPlan } from '@/client/types/promotion-pricing/catalog';
import { getTypeMeta } from './catalog-meta';
import { PlanList } from './plan-list';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';

interface TypeAccordionProps {
  totid: number;
  plans: CatalogPlan[];
  ccode: string;
  open: boolean;
  onToggle: () => void;
  onEditPrice: (plan: CatalogPlan) => void;
  onEditConfig: (plan: CatalogPlan) => void;
  onDelete: (plan: CatalogPlan) => void;
  onCreate: (totid: number) => void;
}

/** Main accordion group (one tradeable-object type) with an animated body. */
export const TypeAccordion = ({
  totid,
  plans,
  ccode,
  open,
  onToggle,
  onEditPrice,
  onEditConfig,
  onDelete,
  onCreate,
}: TypeAccordionProps) => {
  const { allowed: canManage, reason } = useGate(PERMISSIONS.promotion.edit);
  const meta = getTypeMeta(totid);
  const Icon = meta.icon;
  const activeCount = plans.filter((p) => p.active).length;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex w-full items-center gap-3.5 px-5 py-4 transition-colors hover:bg-gray-50/60">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3.5 text-left"
        >
          <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', meta.accent)}>
            <Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-bold text-gray-900">{meta.label}</h2>
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
                {plans.length}
              </span>
            </div>
            <p className="truncate text-xs text-gray-400">
              {meta.blurb} · {activeCount} active
            </p>
          </div>
        </button>

        <button
          type="button"
          disabled={!canManage}
          title={canManage ? undefined : reason}
          onClick={() => canManage && onCreate(totid)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Plan</span>
        </button>

        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? 'Collapse' : 'Expand'}
          className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100"
        >
          <ChevronDown
            className={cn('h-5 w-5 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100">
              <PlanList
                plans={plans}
                ccode={ccode}
                onEditPrice={onEditPrice}
                onEditConfig={onEditConfig}
                onDelete={onDelete}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
